import MarkdownIt from "markdown-it";

/** 各聊天域复用同一安全Markdown规则；扩展引用时使用独立解析器，避免改写普通聊天或共享缓存。 */
export function createChatMarkdownRenderer() {
  const markdown = new MarkdownIt({
    html: false,
    breaks: true,
    linkify: true,
    typographer: false,
  });

  const linkOpen = markdown.renderer.rules.link_open;
  markdown.renderer.rules.link_open = (tokens, index, options, env, renderer) => {
    tokens[index].attrSet("target", "_blank");
    tokens[index].attrSet("rel", "noopener noreferrer");
    return linkOpen
      ? linkOpen(tokens, index, options, env, renderer)
      : renderer.renderToken(tokens, index, options);
  };
  // 文本消息中的图片只显示说明，不因历史消息或逐字输出自动请求外部图片。
  markdown.renderer.rules.image = (tokens, index) =>
    markdown.utils.escapeHtml(tokens[index].content);
  markdown.renderer.rules.table_open = () =>
    '<div class="chat-markdown-table"><table>\n';
  markdown.renderer.rules.table_close = () => "</table></div>\n";
  return markdown;
}
const markdown = createChatMarkdownRenderer();

// 逐字展示时复用未变化的历史消息，避免每个字都重新解析整页聊天记录。
const cache = new Map();
let cacheBytes = 0;
const maxEntries = 64;
const maxBytes = 1024 * 1024;

/** 从完整回答的语法树裁出可见文字，始终保留配对标签，避免逐字解析时闪现星号、链接和表格分隔符。 */
function revealTokens(tokens, budget) {
  const result = [];
  const opened = [];
  for (const token of tokens) {
    if (token.nesting === -1) {
      if (opened.pop()) result.push(token);
      continue;
    }
    if (token.nesting === 1) {
      const visible = budget.remaining > 0;
      opened.push(visible);
      if (visible) result.push(token);
      continue;
    }
    if (budget.remaining <= 0) continue;
    const copy = Object.assign(Object.create(Object.getPrototypeOf(token)), token);
    if (["text", "code_inline", "code_block", "fence", "image"].includes(token.type)) {
      const characters = Array.from(token.content);
      copy.content = characters.slice(0, budget.remaining).join("");
      budget.remaining -= Math.min(characters.length, budget.remaining);
    } else if (token.children) copy.children = revealTokens(token.children, budget);
    else if (["softbreak", "hardbreak"].includes(token.type)) budget.remaining--;
    result.push(copy);
  }
  return result;
}

/** 仅统计可显示字符，不把Markdown标记计入打字进度。 */
function visibleCharacterCount(tokens) {
  return tokens.reduce((count, token) => count + (token.children
    ? visibleCharacterCount(token.children)
    : ["text", "code_inline", "code_block", "fence", "image"].includes(token.type)
      ? Array.from(token.content).length
      : ["softbreak", "hardbreak"].includes(token.type) ? 1 : 0), 0);
}

/** 历史直接使用完整HTML；新回答按进度揭示已解析的文字，不更改消息原文。 */
function renderEntry(entry, progress) {
  if (!Number.isFinite(progress) || progress >= 1) return entry.html;
  const remaining = Math.floor(entry.characters * Math.max(0, progress));
  if (remaining <= 0) return "";
  return markdown.renderer.render(revealTokens(entry.tokens, { remaining }), markdown.options, {});
}

export function renderAgentServiceMarkdown(value, progress = 1) {
  const text = String(value ?? "");
  if (cache.has(text)) {
    const entry = cache.get(text);
    cache.delete(text);
    cache.set(text, entry);
    return renderEntry(entry, progress);
  }
  const tokens = markdown.parse(text, {});
  const html = markdown.renderer.render(tokens, markdown.options, {});
  const entry = { html, tokens, characters: visibleCharacterCount(tokens), bytes: 0 };
  const bytes = (text.length + html.length) * 2;
  entry.bytes = bytes;
  if (bytes <= maxBytes) {
    cache.set(text, entry);
    cacheBytes += bytes;
    while (cache.size > maxEntries || cacheBytes > maxBytes) {
      const oldest = cache.keys().next().value;
      cacheBytes -= cache.get(oldest).bytes;
      cache.delete(oldest);
    }
  }
  return renderEntry(entry, progress);
}
